import { useState, useMemo, type FC } from 'react';
import { PAGE_TYPE } from '@/app/shared/constants/segmentConstants';
import { useAnalytics } from '@/app/shared/hooks/useAnalytics';
import EditIcon from '@/assets/images/edit-icon.svg';
import AddIcon from '@/assets/images/outline-Add.svg';
import { useAppSelector } from '@/store/hooks';
import { selectReferences } from '@/store/reducers/uploadSlice';
import ReferenceCard from './ReferenceCard';
import type { State, RelationshipType, EmergencyContact } from './references.services';

interface ReferenceSectionProps {
  heading: string;
  referenceData?: Partial<EmergencyContact>;
  stateList: State[];
  relationshipList: RelationshipType[];
}

interface TransformedState {
  id: string;
  value: string;
  stateName: string;
}

interface TransformedRelationship {
  id: number;
  value: string;
}

const ReferenceSection: FC<ReferenceSectionProps> = ({ heading, referenceData, stateList, relationshipList }) => {
  const { trackClick } = useAnalytics({page_type: PAGE_TYPE.ILENDING_HUB_UPLOAD});
  const [showModal, setShowModal] = useState(false);
  const contactId = useAppSelector((state) => state.auth.contactId) || '';
  const references = useAppSelector(selectReferences);
  const actualReferences = Object.values(references).filter(
    (ref) => Object.keys(ref).length > 0
  );

  const transformStateList = (list: State[]): TransformedState[] => {
    if (!Array.isArray(list)) return [];
    return list.map((rec) => ({
      id: rec.stateId,
      value: rec.stateName,
      stateName: rec.stateName,
    }));
  };

  const transformRelationshipList = (list: RelationshipType[]): TransformedRelationship[] => {
    if (!Array.isArray(list)) return [];
    return list.map((rec) => ({
      id: rec.relationshipTypeId,
      value: rec.relationShipName,
    }));
  };

  const localStateList = useMemo(() => transformStateList(stateList || []), [stateList]);
  const localRelationshipList = useMemo(() => transformRelationshipList(relationshipList || []), [relationshipList]);

  const handleAddClick = () => {
    trackClick('Emergency Contact Action', {
      action_type: 'add',
      contact_count: actualReferences.length + 1,
      total_required: 3
    });
    setShowModal(true);
  };

  const handleEdit = () => {
    trackClick('Emergency Contact Action', {
      action_type: 'edit',
      contact_count: actualReferences.length,
      total_required: 3
    });
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  const getRelationshipValue = (id: number | undefined): string | undefined => {
    if (Array.isArray(localRelationshipList) && localRelationshipList.length > 0) {
      const rel = localRelationshipList.find((r) => r.id === id || r.id.toString() === String(id));
      return rel ? rel.value : undefined;
    }
    return undefined;
  };

  const getStateValue = (code: string | undefined): string | undefined => {
    if (Array.isArray(localStateList) && localStateList.length > 0) {
      const state = localStateList.find((s) => s.value === code || s.id === code);
      return state ? state.stateName : undefined;
    }
    return undefined;
  };

  return (
    <>
      {referenceData ? (
        <>
          <div className="document-form__edit-section-div">
            <div className="document-form__reference-edit-section">
              <h3>{heading}</h3>
              <span
                onClick={handleEdit}
                className="referenceSection__image-container-edit"
              >
                <img
                  className="referenceSection__add-edit-icon"
                  src={EditIcon}
                  alt="Edit"
                />
              </span>
            </div>

            <div className="document-form__reference-edit-body">
              <span>
                <b>Full Name:</b> {referenceData.fullName}
              </span>
              <span>
                <b>Phone Number:</b> {referenceData.phoneNumber}
              </span>
              <span>
                <b>Email Address:</b> {referenceData.emailAddress}
              </span>
              <span>
                <b>Relationship:</b> {getRelationshipValue(referenceData.relationshipTypeId)}
              </span>
              <span>
                <b>Address:</b> {referenceData.streetLine1}
              </span>
              <span>
                <b>City:</b> {referenceData.city}
              </span>
              <span>
                <b>State:</b> {getStateValue(referenceData.state)}
              </span>
              <span>
                <b>Zip:</b> {referenceData.zip}
              </span>
            </div>
          </div>
        </>
      ) : (
        <div className="referenceSection">
          <div className="referenceHeading">{heading}</div>
          <div className="addbuttondiv">
            <span
              onClick={handleAddClick}
              className="referenceSection__image-container"
            >
              <img
                className="referenceSection__add-edit-icon"
                src={AddIcon}
                alt="Add"
              />
            </span>
          </div>
        </div>
      )}

      <ReferenceCard
        showModal={showModal}
        handleClose={handleClose}
        referenceData={referenceData}
        referenceHeading={heading}
        stateList={stateList}
        relationshipList={relationshipList}
        contactId={contactId || undefined}
      />
    </>
  );
};

export default ReferenceSection;
